import { useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';
import { apiFetch } from '@/lib/api';

/**
 * v0.4 追加7要件② (D-234/D-246): 表示期間に含まれる年ぶんの祝日を取得し
 * 'YYYY-MM-DD' → 祝日名 の Map にまとめて返す。年をまたぐ期間 (年末年始の週表示等) にも
 * 対応するため年単位で複数 fetch する (`GET /tenant-holidays` は year 単位のクエリのみ対応)。
 * node_id を渡すと「テナント既定 ∪ 当該店舗固有」の実効一覧になる (D-234 の additive 仕様)。
 * 設計: docs/design/v04-shift-additional-requirements.md §1-2(b)
 */
type HolidayRow = { holiday_date: string; name: string };
type HolidayListResponse = { holidays: HolidayRow[] };

const YEAR_RE = /^\d{4}/;

/** 純粋関数のためテスト対象としてexport (`useHolidays.test.ts`)。 */
export const yearsInRange = (fromYyyyMmDd: string, toYyyyMmDd: string): string[] => {
  if (!YEAR_RE.test(fromYyyyMmDd) || !YEAR_RE.test(toYyyyMmDd)) return [];
  const fromYear = Number(fromYyyyMmDd.slice(0, 4));
  const toYear = Number(toYyyyMmDd.slice(0, 4));
  const lo = Math.min(fromYear, toYear);
  const hi = Math.max(fromYear, toYear);
  return Array.from({ length: hi - lo + 1 }, (_, i) => String(lo + i));
};

export const useHolidays = (
  fromYyyyMmDd: string,
  toYyyyMmDd: string,
  nodeId?: string | null,
): ReadonlyMap<string, string> => {
  const years = useMemo(() => yearsInRange(fromYyyyMmDd, toYyyyMmDd), [fromYyyyMmDd, toYyyyMmDd]);

  const results = useQueries({
    queries: years.map((year) => ({
      queryKey: ['tenant-holidays', nodeId ?? null, year],
      queryFn: () => apiFetch<HolidayListResponse>(`/tenant-holidays?year=${year}${nodeId ? `&node_id=${nodeId}` : ''}`),
      staleTime: 60 * 60_000,
    })),
  });

  const holidayRows = useMemo(() => results.flatMap((r) => r.data?.holidays ?? []), [results]);

  return useMemo(() => new Map(holidayRows.map((h) => [h.holiday_date, h.name])), [holidayRows]);
};
